import axios, {
  AxiosError,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';
import { env } from '@/shared/lib/env';
import { clearTokens, getAccessToken, getRefreshToken, setTokens } from '@/shared/lib/auth';

type RetriableConfig = InternalAxiosRequestConfig & { _retry?: boolean };
type PendingResolver = (token: string | null) => void;

const REDIRECT_PATH = '/login';

const axiosInstance = axios.create({
  baseURL: env.apiUrl,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30_000,
});

axiosInstance.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

let isRefreshing = false;
let pendingQueue: PendingResolver[] = [];

const flushQueue = (token: string | null): void => {
  pendingQueue.forEach((cb) => cb(token));
  pendingQueue = [];
};

const redirectToLogin = (): void => {
  clearTokens();
  if (typeof window !== 'undefined' && window.location.pathname !== REDIRECT_PATH) {
    window.location.href = REDIRECT_PATH;
  }
};

type RefreshResponse = {
  access_token: string;
  refresh_token: string;
};

const performRefresh = async (refreshToken: string): Promise<RefreshResponse> => {
  const { data } = await axios.post<RefreshResponse>(
    `${env.apiUrl}/auth/refresh`,
    { refresh_token: refreshToken },
    { headers: { 'Content-Type': 'application/json' } },
  );
  return data;
};

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as RetriableConfig | undefined;
    const status = error.response?.status;

    if (!originalRequest || status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    const url = originalRequest.url ?? '';
    // Public, no-auth endpoints must NEVER bounce the visitor to /login. Anyone
    // with the link/QR can open them; just surface the error to the caller.
    if (url.includes('/receipts/public')) {
      return Promise.reject(error);
    }
    if (url.includes('/auth/refresh') || url.includes('/auth/google') || url.includes('/auth/staff/login')) {
      redirectToLogin();
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        pendingQueue.push((token) => {
          if (!token) {
            reject(error);
            return;
          }
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${token}`;
          }
          resolve(axiosInstance(originalRequest as AxiosRequestConfig));
        });
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      isRefreshing = false;
      flushQueue(null);
      redirectToLogin();
      return Promise.reject(error);
    }

    try {
      const { access_token, refresh_token } = await performRefresh(refreshToken);
      setTokens(access_token, refresh_token);
      flushQueue(access_token);
      if (originalRequest.headers) {
        originalRequest.headers.Authorization = `Bearer ${access_token}`;
      }
      return axiosInstance(originalRequest as AxiosRequestConfig);
    } catch (refreshError) {
      flushQueue(null);
      redirectToLogin();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  },
);

export default axiosInstance;
